/**
 * CampaignEditor — full-page create + edit experience.
 *
 * Three columns of work:
 *   1. Details (title, advertiser, period, categories, description)
 *   2. Expected layout — picker over the 6 platform layouts. Drives
 *      which zones the asset editor surfaces AND the live preview's
 *      rectangles. Empty layout = "fullscreen / I'll decide later"
 *      with one default `main` slot.
 *   3. Per-zone asset uploader, scoped to the chosen layout's zones.
 *
 * Right rail: **live preview** — renders each zone as a positioned
 * rectangle in the layout's geometry, with the assigned asset shown
 * (image thumbnail / video poster / empty slot hint). This is the
 * "how my campaign will run" view the publisher asked for.
 *
 * Used for both /campaigns/new and /campaigns/:campaignId/edit. New
 * mode hides the asset section + preview until the campaign is saved
 * once (assets require an id to POST against).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import {
  Advertiser,
  Campaign,
  CampaignAsset,
  CampaignAssetType,
  CreateCampaignAssetRequest,
  Layout,
  LayoutZone,
} from '../types';
import {
  AssetGrid,
  FULLSCREEN_FALLBACK,
  LayoutOption,
  PlayingHoursEditor,
  PreviewStage,
  SectionTitle,
} from '../components/campaign/campaignEditorShared';

const CampaignEditor: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const basePath = `/${user?.role || 'admin'}`;
  const isCreate = !campaignId;

  // Core data
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [layouts, setLayouts] = useState<Layout[]>([]);

  // Client-side staging for assets uploaded BEFORE the campaign exists.
  // In create mode, the asset editor is functional from the first
  // render — files go to S3 immediately, but the asset-row POST is
  // deferred until createCampaign returns an id (then we replay them
  // and route to /edit). Cleared after a successful create.
  const [pendingAssets, setPendingAssets] = useState<CampaignAsset[]>([]);
  const [uploadingZone, setUploadingZone] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [categories, setCategories] = useState('OTHER');
  const [targetLayoutId, setTargetLayoutId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [playingHours, setPlayingHours] = useState('');

  // UI flags
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Live-preview controls. Default to paused so the editor doesn't
  // start hammering S3 the moment it mounts.
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewResetTick, setPreviewResetTick] = useState(0);

  // ---- bootstrap ----

  useEffect(() => {
    apiService
      .listAdvertisersV2({ limit: 200 })
      .then((r: any) => setAdvertisers(r.data || []))
      .catch(() => setAdvertisers([]));
    apiService
      .listLayouts()
      .then(setLayouts)
      .catch(() => setLayouts([]));
  }, []);

  useEffect(() => {
    if (isCreate) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await apiService.getCampaign(campaignId!);
        if (cancelled) return;
        setCampaign(c);
        setTitle(c.title);
        setDescription(c.description || '');
        setAdvertiserId(c.advertiser_id || '');
        setCategories(c.categories || 'OTHER');
        setTargetLayoutId(c.target_layout_id || '');
        setPlayingHours(c.playing_hours || '');
        setStartDate((c.published_time_start || '').slice(0, 10));
        setEndDate((c.published_time_end || '').slice(0, 10));
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, isCreate]);

  // ---- derived ----

  const activeLayout = useMemo<Layout | null>(() => {
    if (!targetLayoutId) return null;
    return layouts.find((l) => l.id === targetLayoutId) || null;
  }, [layouts, targetLayoutId]);

  /** Zones to surface in the asset editor + preview. */
  const editableZones = useMemo<LayoutZone[]>(
    () => activeLayout?.zones || [FULLSCREEN_FALLBACK],
    [activeLayout],
  );

  // ---- handlers ----

  const handleSave = async (): Promise<Campaign | null> => {
    if (!title || !startDate || !endDate) {
      setError('Title, start date and end date are required.');
      return null;
    }
    if (isCreate && !advertiserId) {
      setError('Pick an advertiser.');
      return null;
    }
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        const created = await apiService.createCampaign({
          advertiser_id: advertiserId,
          title,
          description,
          categories,
          target_layout_id: targetLayoutId || undefined,
          playing_hours: playingHours,
          published_time_start: `${startDate}T00:00:00Z`,
          published_time_end: `${endDate}T23:59:59Z`,
        });
        // Commit any client-side-staged assets that were uploaded
        // before the campaign existed. Sequential rather than parallel
        // so sort_order stays deterministic if two slots target the
        // same zone.
        let committed = 0;
        for (const a of pendingAssets) {
          try {
            await apiService.addCampaignAsset(created.id, {
              zone_slug: a.zone_slug,
              content_url: a.content_url,
              content_type: a.content_type,
              duration_seconds: a.duration_seconds,
              sort_order: a.sort_order,
              sha256: a.sha256,
              size_bytes: a.size_bytes,
            });
            committed++;
          } catch (e: any) {
            // Don't bail — best-effort, report the count in the toast
            // so the user knows to retry from the asset grid.
            setError(
              `Some assets failed to attach (${
                pendingAssets.length - committed
              } left). ${e?.response?.data?.error || ''}`.trim(),
            );
          }
        }
        // Refresh the canonical campaign before navigating so the
        // /edit page picks up the committed assets.
        const fresh = await apiService.getCampaign(created.id);
        setCampaign(fresh);
        setPendingAssets([]);
        setSuccess(
          committed > 0
            ? `Campaign created with ${committed} asset${committed === 1 ? '' : 's'}.`
            : 'Campaign created — now add an asset for each zone.',
        );
        navigate(`${basePath}/campaigns/${created.id}`, { replace: true });
        return fresh;
      } else {
        const updated = await apiService.updateCampaign(campaign!.id, {
          title,
          description,
          categories,
          target_layout_id: targetLayoutId || '',
          playing_hours: playingHours,
          published_time_start: `${startDate}T00:00:00Z`,
          published_time_end: `${endDate}T23:59:59Z`,
        });
        setCampaign(updated);
        setSuccess('Campaign updated.');
        return updated;
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!campaign) return;
    setPublishing(true);
    try {
      const updated = await apiService.publishCampaign(campaign.id);
      setCampaign(updated);
      setSuccess('Campaign published. You can submit it to venues next.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // ---- asset handlers (shared across create + edit modes) ----

  const displayedAssets: CampaignAsset[] = useMemo(
    () => (campaign ? campaign.assets || [] : pendingAssets),
    [campaign, pendingAssets],
  );

  /** Mints a CampaignAsset-shaped placeholder while we wait for the
   *  real backend row. Same fields the renderer reads; the id prefix
   *  marks it as pending so handlers can branch. */
  const makePendingAsset = (
    payload: CreateCampaignAssetRequest,
  ): CampaignAsset => ({
    id: `pending-${Math.random().toString(36).slice(2)}`,
    campaign_id: '',
    zone_slug: payload.zone_slug,
    content_url: payload.content_url,
    content_type: payload.content_type,
    duration_seconds: payload.duration_seconds ?? 8,
    sort_order: payload.sort_order ?? 0,
    sha256: payload.sha256,
    size_bytes: payload.size_bytes,
    version: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const isPending = (a: CampaignAsset) => a.id.startsWith('pending-');

  /** Reads a video file's runtime locally so we don't ship a wrong
   *  duration_seconds to the backend. The browser's <video> element
   *  loads just the metadata (no full download) and exposes `duration`
   *  in seconds. Falls back to a safe default if the codec is exotic
   *  or the file is malformed — better than blocking the upload. */
  const probeVideoDuration = (file: File): Promise<number> =>
    new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      const url = URL.createObjectURL(file);
      const done = (sec: number) => {
        URL.revokeObjectURL(url);
        resolve(sec);
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        done(Number.isFinite(d) && d > 0 ? Math.round(d) : 10);
      };
      v.onerror = () => done(10);
      v.src = url;
    });

  /** Upload to S3 and either POST an asset row (edit mode) or stage
   *  client-side (create mode). The page-level state owns this so the
   *  asset section is functional from the very first render of a new
   *  campaign — no "save first" hand-waving. */
  /** Add an asset by pasting an externally-hosted URL. Skips the
   *  bucket round-trip entirely — useful when a publisher already
   *  hosts the creative on their own CDN, or when a creative was
   *  promoted from another campaign and we want to reference the
   *  exact same URL instead of duplicating bytes in S3. Content type
   *  is inferred from the path extension; an HTMLVideoElement probes
   *  the duration for videos so the rotation timing is right. */
  const handleAddByUrl = useCallback(
    async (rawUrl: string, zoneSlug: string) => {
      const url = rawUrl.trim();
      if (!url) return;
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          setError('URL must start with http:// or https://');
          return;
        }
      } catch {
        setError('That doesn\'t look like a valid URL.');
        return;
      }
      const lower = url.toLowerCase().split('?')[0];
      const isVideo =
        lower.endsWith('.mp4') ||
        lower.endsWith('.mov') ||
        lower.endsWith('.m4v') ||
        lower.endsWith('.webm') ||
        lower.endsWith('.mkv');
      const isImage =
        lower.endsWith('.jpg') ||
        lower.endsWith('.jpeg') ||
        lower.endsWith('.png') ||
        lower.endsWith('.gif') ||
        lower.endsWith('.webp');
      if (!isVideo && !isImage) {
        setError(
          'URL must end with a recognised image or video extension ' +
            '(.mp4 / .mov / .webm / .jpg / .png / .webp / …).',
        );
        return;
      }
      const contentType: CampaignAssetType = isVideo ? 'VIDEO' : 'IMAGE';
      const detected = isVideo ? await probeVideoDurationFromUrl(url) : 8;
      const sortBase = displayedAssets.filter(
        (a) => a.zone_slug === zoneSlug,
      ).length;
      const payload: CreateCampaignAssetRequest = {
        zone_slug: zoneSlug,
        content_url: url,
        content_type: contentType,
        duration_seconds: detected,
        sort_order: sortBase,
        // No upload happened → we don't have a server-computed
        // integrity hash or size. Leave empty; gb-media uses them
        // opportunistically, not as gates.
        sha256: '',
        size_bytes: 0,
      };
      try {
        if (campaign) {
          await apiService.addCampaignAsset(campaign.id, payload);
          const fresh = await apiService.getCampaign(campaign.id);
          setCampaign(fresh);
        } else {
          setPendingAssets((prev) => [...prev, makePendingAsset(payload)]);
        }
      } catch (e: any) {
        setError(
          e?.response?.data?.error || e?.message || 'Add by URL failed',
        );
      }
    },
    [campaign, displayedAssets],
  );

  /** Same probe as the upload path, but loads the URL directly so we
   *  don't need a File handle. Times out at 5s — most CDN-hosted
   *  creatives have metadata at the head of the file and respond in
   *  under a second; if it doesn't we fall back to 10s default. */
  const probeVideoDurationFromUrl = (url: string): Promise<number> =>
    new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      let settled = false;
      const done = (sec: number) => {
        if (settled) return;
        settled = true;
        resolve(sec);
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        done(Number.isFinite(d) && d > 0 ? Math.round(d) : 10);
      };
      v.onerror = () => done(10);
      setTimeout(() => done(10), 5000);
      v.src = url;
    });

  const handleUpload = useCallback(
    async (file: File, zoneSlug: string) => {
      setUploadingZone(zoneSlug);
      try {
        const up: any = await apiService.uploadFile(file);
        const contentType: CampaignAssetType =
          file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
        // Auto-probe video duration from file metadata so the safety
        // watchdog in gb-media (= duration + 30s) doesn't truncate a
        // long video. Images keep the 8s default — publishers edit it
        // inline if they want a different dwell.
        const detected =
          contentType === 'VIDEO' ? await probeVideoDuration(file) : 8;
        const sortBase = displayedAssets.filter(
          (a) => a.zone_slug === zoneSlug,
        ).length;
        const payload: CreateCampaignAssetRequest = {
          zone_slug: zoneSlug,
          content_url: up.url,
          content_type: contentType,
          duration_seconds: detected,
          sort_order: sortBase,
          sha256: up.sha256 || '',
          size_bytes: up.size_bytes || 0,
        };
        if (campaign) {
          await apiService.addCampaignAsset(campaign.id, payload);
          const fresh = await apiService.getCampaign(campaign.id);
          setCampaign(fresh);
        } else {
          setPendingAssets((prev) => [...prev, makePendingAsset(payload)]);
        }
      } catch (e: any) {
        // Surface client-side validation errors (e.g. 100 MB size cap
        // thrown from apiService.uploadFile) via e.message; server
        // errors still come through .response.data.error first.
        setError(
          e?.response?.data?.error || e?.message || 'Upload failed',
        );
      } finally {
        setUploadingZone(null);
      }
    },
    [campaign, displayedAssets],
  );

  const handleRemoveAsset = useCallback(
    async (asset: CampaignAsset) => {
      if (!window.confirm(`Remove this asset from zone "${asset.zone_slug}"?`))
        return;
      try {
        if (isPending(asset)) {
          setPendingAssets((prev) => prev.filter((a) => a.id !== asset.id));
          return;
        }
        if (campaign) {
          await apiService.deleteCampaignAsset(campaign.id, asset.id);
          const fresh = await apiService.getCampaign(campaign.id);
          setCampaign(fresh);
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Delete failed');
      }
    },
    [campaign],
  );

  /** Persist a duration_seconds edit. Staged (pre-create) assets get
   *  patched in-place; saved assets PUT to the asset endpoint and
   *  the campaign is re-fetched so the version + updated_at bumps
   *  surface immediately. */
  const handleUpdateDuration = useCallback(
    async (asset: CampaignAsset, seconds: number) => {
      try {
        if (isPending(asset)) {
          setPendingAssets((prev) =>
            prev.map((a) =>
              a.id === asset.id ? { ...a, duration_seconds: seconds } : a,
            ),
          );
          return;
        }
        if (!campaign) return;
        await apiService.updateCampaignAsset(campaign.id, asset.id, {
          zone_slug: asset.zone_slug,
          content_url: asset.content_url,
          content_type: asset.content_type,
          duration_seconds: seconds,
          sort_order: asset.sort_order,
          sha256: asset.sha256,
          size_bytes: asset.size_bytes,
        });
        const fresh = await apiService.getCampaign(campaign.id);
        setCampaign(fresh);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Update failed');
      }
    },
    [campaign],
  );

  const refreshAssets = useCallback(async () => {
    if (!campaign) return;
    try {
      const fresh = await apiService.getCampaign(campaign.id);
      setCampaign(fresh);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Refresh failed');
    }
  }, [campaign]);

  // ---- render ----

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Button
        onClick={() => navigate(`${basePath}/campaigns`)}
        sx={{ color: '#1D4ED8', mb: 1, textTransform: 'none' }}
      >
        ← Campaigns
      </Button>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} sx={{ mb: 3 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#2563EB',
              mb: 0.5,
            }}
          >
            {isCreate ? 'New campaign' : 'Edit campaign'}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
            {title || (isCreate ? 'Untitled campaign' : '')}
          </Typography>
          {campaign && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={campaign.state}
                size="small"
                color={
                  campaign.state === 'PUBLISHED'
                    ? 'success'
                    : campaign.state === 'REJECTED'
                    ? 'error'
                    : 'default'
                }
              />
              {activeLayout && (
                <Chip
                  label={`Layout · ${activeLayout.display_name}`}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${campaign.assets?.length || 0} asset${
                  (campaign.assets?.length || 0) === 1 ? '' : 's'
                }`}
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: { xs: 2, md: 0 } }}>
          {campaign?.state === 'DRAFT' && (campaign.assets?.length || 0) > 0 && (
            <Button
              variant="outlined"
              startIcon={<LaunchIcon />}
              onClick={handlePublish}
              disabled={publishing}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          )}
          {campaign?.state === 'PUBLISHED' && (campaign.assets?.length || 0) > 0 && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() =>
                navigate(`${basePath}/campaigns/${campaign.id}/submit`)
              }
            >
              Submit to venues
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isCreate ? 'Create campaign' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 420px' },
          gap: 4,
          alignItems: 'flex-start',
        }}
      >
        {/* ---- left column: details + layout + assets ---- */}
        <Box>
          {/* Details */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Details</SectionTitle>
            <Stack spacing={2}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {isCreate ? (
                <FormControl fullWidth>
                  <InputLabel>Advertiser</InputLabel>
                  <Select
                    value={advertiserId}
                    label="Advertiser"
                    onChange={(e) => setAdvertiserId(e.target.value as string)}
                  >
                    {advertisers.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Advertiser"
                  fullWidth
                  value={
                    advertisers.find((a) => a.id === advertiserId)?.display_name ||
                    advertiserId
                  }
                  disabled
                  helperText="Advertiser cannot be changed after creation"
                />
              )}
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <TextField
                label="Categories (CSV)"
                fullWidth
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                helperText="e.g. FNB, BEAUTY"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <TextField
                  label="End"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Playing hours */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Playing hours</SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              When during the day this campaign is eligible to play. The
              outer date range above is the campaign's whole booking
              window; this is the per-day slot inside it. Devices apply
              their own local clock when evaluating.
            </Typography>
            <PlayingHoursEditor
              value={playingHours}
              onChange={setPlayingHours}
            />
          </Paper>

          {/* Expected layout */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Expected layout</SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              Pick the layout your campaign is designed for. The editor below
              will surface one slot per zone and the preview shows how it'll
              play. You can change this at any time before publishing.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 1.5,
              }}
            >
              <LayoutOption
                label="Fullscreen (default)"
                slug="—"
                zones={[FULLSCREEN_FALLBACK]}
                selected={!targetLayoutId}
                onSelect={() => setTargetLayoutId('')}
              />
              {layouts.map((l) => (
                <LayoutOption
                  key={l.id}
                  label={l.display_name}
                  slug={l.slug}
                  zones={l.zones}
                  selected={targetLayoutId === l.id}
                  onSelect={() => setTargetLayoutId(l.id)}
                />
              ))}
            </Box>
          </Paper>

          {/* Assets — fully functional from first render in create mode.
              Uploads stream to S3 immediately and stage client-side; on
              first Save the staged rows attach to the new campaign. */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>
              Assets by zone — {activeLayout?.display_name || 'fullscreen'}
            </SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              One creative per zone. Files upload to S3 right away;
              SHA-256 + size are stamped for cache validation on the
              player. {isCreate &&
                'Uploads here will be attached to the campaign when you click Create.'}
            </Typography>
            <AssetGrid
              zones={editableZones}
              assets={displayedAssets}
              uploadingZone={uploadingZone}
              onUpload={handleUpload}
              onAddByUrl={handleAddByUrl}
              onRemove={handleRemoveAsset}
              onDurationChange={handleUpdateDuration}
            />
          </Paper>
        </Box>

        {/* ---- right rail: live preview ---- */}
        <Box sx={{ position: { md: 'sticky' }, top: 24 }}>
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 2px rgba(120,90,60,0.06)' }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#2563EB',
                }}
              >
                Live preview
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={previewPlaying ? 'Pause' : 'Play'}>
                  <IconButton
                    size="small"
                    onClick={() => setPreviewPlaying((p) => !p)}
                    disabled={(campaign?.assets?.length || 0) === 0}
                  >
                    {previewPlaying ? (
                      <PauseIcon fontSize="small" />
                    ) : (
                      <PlayArrowIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Restart from beginning">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewResetTick((t) => t + 1)}
                    disabled={(campaign?.assets?.length || 0) === 0}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              {activeLayout
                ? `${activeLayout.display_name} · ${activeLayout.zones.length} zone${
                    activeLayout.zones.length === 1 ? '' : 's'
                  }`
                : 'Fullscreen (single main zone)'}
            </Typography>
            <PreviewStage
              zones={editableZones}
              assets={campaign?.assets || []}
              playing={previewPlaying}
              resetTick={previewResetTick}
            />
            <Typography
              sx={{
                fontSize: 11,
                color: '#94A3B8',
                mt: 1.5,
                textAlign: 'center',
              }}
            >
              16:9 aspect · videos muted in preview · zones rotate per
              asset duration
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};


export default CampaignEditor;
